import React, { useState, useEffect } from 'react';
import { Square, Circle, Triangle, Play } from 'lucide-react';

// Custom hook for responsive breakpoints
const useResponsive = () => {
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    screenWidth,
    isMobile: screenWidth < 640,
    isTablet: screenWidth >= 640 && screenWidth < 1024,
    isDesktop: screenWidth >= 1024,
    isSmall: screenWidth < 375,
    isLarge: screenWidth >= 1440
  };
};

const Podcasts = () => {
  const { isMobile, isTablet, isDesktop, isSmall } = useResponsive();

  const podcastData = [
    {
      title: 'AFTERHOURS: NIGHT 1',
      date: 'August 1, 2025',
      icon: 'square',
      id: 1
    },
    {
      title: 'CLUB REMIX TALKS',
      date: 'July 28, 2025',
      icon: 'circle',
      id: 2
    },
    {
      title: 'BACKSTAGE MIXES',
      date: 'July 21, 2025',
      icon: 'triangle',
      id: 3
    }
  ];

  const getPodcastStyles = () => ({
    container: {
      width: '100%',
      backgroundColor: '#000000',
      padding: isMobile ? '40px 16px' : isTablet ? '60px 24px' : '80px 32px',
      minHeight: isMobile ? 'auto' : '600px',
      boxSizing: 'border-box'
    },
    wrapper: {
      maxWidth: '1280px',
      margin: '0 auto',
      width: '100%'
    },
    header: {
      marginBottom: isMobile ? '32px' : isTablet ? '40px' : '48px'
    },
    title: {
      fontSize: isSmall ? '32px' : isMobile ? '36px' : isTablet ? '48px' : '56px',
      fontWeight: '900',
      color: '#FFFFFF',
      textTransform: 'uppercase',
      letterSpacing: isMobile ? '1px' : '2px',
      marginBottom: isMobile ? '8px' : '12px',
      lineHeight: '1.1'
    },
    subtitle: {
      fontSize: isSmall ? '14px' : isMobile ? '16px' : isTablet ? '18px' : '20px',
      fontWeight: '600',
      color: '#666666',
      textTransform: 'uppercase',
      letterSpacing: isMobile ? '0.5px' : '1px',
      margin: 0
    },
    podcastList: {
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? '24px' : '32px'
    },
    podcastItem: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '16px' : '24px',
      padding: isMobile ? '16px' : '20px',
      borderRadius: '12px',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      backgroundColor: 'transparent'
    },
    podcastItemHover: {
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      transform: 'translateY(-2px)'
    },
    iconContainer: {
      width: isMobile ? '60px' : isTablet ? '70px' : '80px',
      height: isMobile ? '60px' : isTablet ? '70px' : '80px',
      backgroundColor: '#DC2626',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      position: 'relative',
      overflow: 'hidden'
    },
    iconShape: {
      color: '#333333',
      width: isMobile ? '24px' : '28px',
      height: isMobile ? '24px' : '28px'
    },
    content: {
      flex: 1,
      minWidth: 0
    },
    podcastTitle: {
      fontSize: isSmall ? '16px' : isMobile ? '18px' : isTablet ? '22px' : '24px',
      fontWeight: '700',
      color: '#FFFFFF',
      textTransform: 'uppercase',
      letterSpacing: isMobile ? '0.3px' : '0.5px',
      marginBottom: isMobile ? '4px' : '6px',
      lineHeight: '1.2'
    },
    podcastDate: {
      fontSize: isSmall ? '12px' : isMobile ? '14px' : isTablet ? '16px' : '16px',
      fontWeight: '500',
      color: '#666666',
      letterSpacing: '0.3px'
    },
    playButton: {
      width: isMobile ? '40px' : '48px',
      height: isMobile ? '40px' : '48px',
      borderRadius: '50%',
      backgroundColor: 'rgba(220, 38, 38, 0.1)',
      border: '2px solid #DC2626',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    playButtonHover: {
      backgroundColor: '#DC2626',
      transform: 'scale(1.1)'
    }
  });

  const renderIcon = (iconType, style) => {
    switch (iconType) {
      case 'square':
        return <Square style={style} fill="currentColor" />;
      case 'circle':
        return <Circle style={style} fill="currentColor" />;
      case 'triangle':
        return <Triangle style={style} fill="currentColor" />;
      default:
        return <Square style={style} fill="currentColor" />;
    }
  };

  const styles = getPodcastStyles();

  return (
    <div style={styles.container}>
      <div style={styles.wrapper}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            PODCASTS
          </h2>
          <p style={styles.subtitle}>
            HOTMESS HIGHLIGHTS
          </p>
        </div>
        
        <div style={styles.podcastList}>
          {podcastData.map((podcast) => {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const [isHovered, setIsHovered] = React.useState(false);
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const [isPlayHovered, setIsPlayHovered] = React.useState(false);
            
            return (
              <div
                key={podcast.id}
                style={{
                  ...styles.podcastItem,
                  ...(isHovered ? styles.podcastItemHover : {})
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <div style={styles.iconContainer}>
                  {renderIcon(podcast.icon, styles.iconShape)}
                </div>
                
                <div style={styles.content}>
                  <h3 style={styles.podcastTitle}>
                    {podcast.title}
                  </h3>
                  <p style={styles.podcastDate}>
                    {podcast.date}
                  </p>
                </div>
                
                <button
                  style={{
                    ...styles.playButton,
                    ...(isPlayHovered ? styles.playButtonHover : {})
                  }}
                  onMouseEnter={() => setIsPlayHovered(true)}
                  onMouseLeave={() => setIsPlayHovered(false)}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Handle play functionality here
                  }}
                >
                  <Play 
                    size={isMobile ? 16 : 20} 
                    color={isPlayHovered ? '#FFFFFF' : '#DC2626'}
                    fill={isPlayHovered ? '#FFFFFF' : '#DC2626'}
                    style={{ marginLeft: '2px' }}
                  />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Podcasts;